import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Upload, FileText } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";

const sel = "mt-1 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm";

export function ClassifyDialog({ customer, classes, onClose, onDone }) {
  const [f, setF] = useState({ fiscal_class: customer.customer_type || "regular", country: customer.country || "", foreign_id: customer.foreign_id || "", reason: "" });
  const [doc, setDoc] = useState({ file: null, doc_type: "Resolución", doc_number: "", expires_at: "" });
  const isE44 = classes.find((c) => c.value === f.fiscal_class)?.e44;
  const hasDoc = customer.exemption?.status === "aprobado";
  const confirm = useConfirm();
  const saveClass = async () => {
    try { await api.put(`/customers/${customer.id}/fiscal-class`, f); toast.success("Clasificación guardada"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const uploadDoc = async () => {
    if (!doc.file) return toast.error("Selecciona el PDF o la foto del documento");
    const fd = new FormData();
    fd.append("file", doc.file); fd.append("doc_type", doc.doc_type); fd.append("doc_number", doc.doc_number);
    fd.append("expires_at", doc.expires_at); fd.append("fiscal_class_value", isE44 ? f.fiscal_class : "zona_franca");
    try {
      await api.post(`/customers/${customer.id}/exemption-doc`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento validado con el padrón DGII · cliente elegible para E44"); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="fiscal-classify-dialog">
        <DialogHeader><DialogTitle>Clasificación fiscal · {customer.name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="overline">Tipo de cliente</Label>
            <select className={sel} value={f.fiscal_class} onChange={(e) => setF({ ...f, fiscal_class: e.target.value })} data-testid="fiscal-class-select">
              {classes.map((c) => <option key={c.value} value={c.value}>{c.label} · {(c.vouchers || []).map((v) => v.type).join(" / ")}</option>)}
            </select></div>
          <VoucherInfo cls={classes.find((c) => c.value === f.fiscal_class)} />
          {f.fiscal_class === "internacional" && (<>
            <div><Label className="overline">País (ISO)</Label><Input className="mt-1 uppercase" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} data-testid="fiscal-country" /></div>
            <div><Label className="overline">Id. fiscal extranjera</Label><Input className="mt-1" value={f.foreign_id} onChange={(e) => setF({ ...f, foreign_id: e.target.value })} data-testid="fiscal-foreign-id" /></div>
          </>)}
          <div className="col-span-2"><Label className="overline">Motivo</Label><Input className="mt-1" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="fiscal-reason" /></div>
          <p className="col-span-2 text-xs text-muted-foreground">Régimen especial y gobierno se validan contra el padrón DGII: RNC activo y razón social igual.</p>
        </div>
        <DialogFooter><Button className="bg-primary font-bold" onClick={saveClass} data-testid="fiscal-class-save"><ShieldCheck className="w-4 h-4 mr-1.5" />Guardar clasificación</Button></DialogFooter>
        {(isE44 || hasDoc) && <div className="border-t pt-3 space-y-2" data-testid="fiscal-doc-section">
          <div className="overline">Documento que acredita la exención (E44)</div>
          {!hasDoc && <p className="text-xs text-muted-foreground">Sube la resolución, carta o carnet (PDF/JPG/PNG). Al validarse con el padrón DGII, el cliente queda clasificado y el POS le permite E44.</p>}
          {customer.exemption?.status === "aprobado" && (
            <a className="text-xs text-primary flex items-center gap-1" href="#" data-testid="fiscal-doc-view" onClick={async (e) => {
              e.preventDefault();
              const { data } = await api.get(`/customers/${customer.id}/exemption-doc/file`, { responseType: "blob" });
              window.open(URL.createObjectURL(data), "_blank");
            }}><FileText className="w-3.5 h-3.5" />{customer.exemption.doc_type} {customer.exemption.doc_number} · vence {customer.exemption.expires_at || "—"}</a>
          )}
          {customer.exemption?.status === "aprobado" && (
            <Button size="sm" variant="ghost" className="text-destructive" data-testid="fiscal-doc-revoke" onClick={async () => {
              if (!(await confirm({ title: "¿Revocar exención?", description: "El cliente vuelve a 'Cliente normal' y deja de ser elegible para E44 (salvo que su RNC esté en la lista autorizada).", confirmLabel: "Revocar", destructive: true }))) return;
              try { await api.delete(`/customers/${customer.id}/exemption-doc`); toast.success("Exención revocada"); onDone(); }
              catch (e) { toast.error(formatApiError(e)); }
            }}>Revocar exención</Button>
          )}
          <div className="grid grid-cols-3 gap-2">
            <select className={sel} value={doc.doc_type} onChange={(e) => setDoc({ ...doc, doc_type: e.target.value })} data-testid="fiscal-doc-type">
              {["Resolución", "Carta", "Carnet", "Certificado"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <Input className="mt-1" placeholder="Número" value={doc.doc_number} onChange={(e) => setDoc({ ...doc, doc_number: e.target.value })} data-testid="fiscal-doc-number" />
            <Input className="mt-1" type="date" value={doc.expires_at} onChange={(e) => setDoc({ ...doc, expires_at: e.target.value })} data-testid="fiscal-doc-expires" />
          </div>
          <Input type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setDoc({ ...doc, file: e.target.files?.[0] })} data-testid="fiscal-doc-file" />
          <Button variant="outline" onClick={uploadDoc} data-testid="fiscal-doc-upload"><Upload className="w-4 h-4 mr-1.5" />Subir y validar</Button>
        </div>}
      </DialogContent>
    </Dialog>
  );
}

export function CustomerClassTab() {
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const load = async () => setRows((await api.get("/customers")).data);
  useEffect(() => { load(); api.get("/fiscal/classes").then(({ data }) => setClasses(data)); }, []);
  const label = (v) => classes.find((c) => c.value === ({ special_regime: "zona_franca", government: "gobierno", foreign: "internacional" }[v] || v))?.label || "Cliente normal";
  const shown = rows.filter((r) => !q || `${r.name} ${r.rnc_cedula}`.toLowerCase().includes(q.toLowerCase())).slice(0, 100);
  return (
    <div className="space-y-3">
      <Input placeholder="Buscar cliente o RNC" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm" data-testid="fiscal-customer-search" />
      <table className="w-full text-sm border border-border rounded-md" data-testid="fiscal-customers-table">
        <thead className="overline border-b"><tr><th className="p-2 text-left">Cliente</th><th className="p-2 text-left">RNC</th><th className="p-2 text-left">Clasificación</th><th className="p-2 text-left">Exención</th><th className="p-2" /></tr></thead>
        <tbody>{shown.map((r) => (
          <tr key={r.id} className="border-b border-border/60">
            <td className="p-2">{r.name}</td><td className="p-2 font-mono text-xs">{r.rnc_cedula || "—"}</td>
            <td className="p-2 text-xs" data-testid={`fiscal-class-${r.id}`}>{label(r.customer_type)}</td>
            <td className="p-2 text-xs">{r.exemption?.status === "aprobado" ? `${r.exemption.doc_type} ${r.exemption.doc_number || ""}` : "—"}</td>
            <td className="p-2 text-right"><Button size="sm" variant="ghost" onClick={() => setEdit(r)} data-testid={`fiscal-classify-${r.id}`}>Clasificar</Button></td>
          </tr>))}</tbody>
      </table>
      {edit && <ClassifyDialog customer={edit} classes={classes} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function VoucherInfo({ cls }) {
  if (!cls?.vouchers) return null;
  return (
    <div className="col-span-2 rounded-md border border-border bg-muted/40 p-3 space-y-2" data-testid="fiscal-class-vouchers">
      <div className="overline">Comprobantes que aplica</div>
      <div className="flex flex-wrap gap-1.5">
        {cls.vouchers.map((v, i) => (
          <span key={v.type} className={`text-xs px-2 py-0.5 rounded font-semibold ${i === 0 ? "bg-primary text-primary-foreground" : "bg-background border border-border"}`}
            data-testid={`fiscal-voucher-${v.type}`}>
            {v.type} · {v.name}{i === 0 ? " (sugerido)" : ""}
          </span>
        ))}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="fiscal-class-note">{cls.note}</p>
    </div>
  );
}
